/**
 * PATH: packages/email-hub/src/feature/reader-header/placements/PlacementManager.tsx
 * FILE_ID: EMAILHUB-READERHEADER-PLACEMENT-MANAGER-001
 * PURPOSE: Summarize one primary placement plus +N and manage the full plural list.
 * ROLE IN ARCHITECTURE: Reader UI adapter over shared Kanban allocation primitives.
 * MAIN EXPORTS: ReaderPlacementsControl.
 * NON-RESPONSIBILITIES: Kanban APIs own permissions, validation, and persistence;
 *   the dialog body lives in PlacementManagerPanel (500-line ceiling split).
 * NOTES FOR FUTURE AI: Never render every placement at rest; keep plural state
 *   in the manager. Zero-placement at-rest is AllocateChip (one click → board
 *   menu), NOT an empty manager hop (BUG-2026-08-03-PLACEMENT-ADD-TWO-CLICK).
 *   Every settled placement mutation publishes a multi-tab sync event so
 *   sibling tabs reconcile once (runtime/multiTabSync.ts).
 */
import { useState } from 'react'
import { Layers3 } from 'lucide-react'
import {
  AllocateChip,
  AllocationChip,
  type getSamKanbanApi,
} from '@quotech/kanban-host'
import type { EmailAllocationSummary } from '@quotech/tenant-shared/email/api'

import { cn } from '../../../_internal/lib/utils'
import type { Email } from '../../hooks/useEmails'
import type { ReaderHeaderSurface } from '../model/types'
import {
  selectPrimaryPlacement,
  selectReaderPlacements,
} from '../model/placements'
import { publishReaderMutation } from '../runtime/multiTabSync'
import { usePrimaryOverride } from './primaryOverride'
import { PlacementManagerPanel } from './PlacementManagerPanel'

export interface ReaderPlacementsControlProps {
  email: Email
  api: ReturnType<typeof getSamKanbanApi>
  orgId: number | null
  onPatchEmail?: (emailId: string, patch: Partial<Email>) => void
  onChanged?: () => void
  onOpenCard?: (cardId: string) => void
  onOpenBoard?: (placement: EmailAllocationSummary) => void
  /**
   * While the decision line owns the ONE confirm/reject pair (D269-12), the
   * at-rest chip must not render its own AI tick/cross. Uses
   * AllocationChip.hideReviewControls — NOT compact — so the reader still
   * shows board · lane (BUG: compact lane-only labels looked like orphan
   * "Priority" tags with no board; change #431).
   */
  suppressReviewControls?: boolean
  /** Optional controlled Manager open state — the FiledTo lane menu opens
   *  the Manager imperatively (change #269 P4). Omit for internal state. */
  manageOpen?: boolean
  onManageOpenChange?: (open: boolean) => void
  /** Telemetry surface label for the manager's action runtime. */
  surface?: ReaderHeaderSurface
}

export function ReaderPlacementsControl({
  email,
  api,
  orgId,
  onPatchEmail,
  onChanged,
  onOpenCard,
  onOpenBoard,
  suppressReviewControls = false,
  manageOpen,
  onManageOpenChange,
  surface,
}: ReaderPlacementsControlProps) {
  const [internalOpen, setInternalOpen] = useState(false)
  // Controlled/uncontrolled hybrid: a provided manageOpen wins; internal
  // state keeps every legacy call site working unchanged.
  const open = manageOpen ?? internalOpen
  const setOpen = (next: boolean) => {
    onManageOpenChange?.(next)
    if (manageOpen === undefined) setInternalOpen(next)
  }
  const placements = selectReaderPlacements(email)
  const overrideId = usePrimaryOverride(email.id)
  const primary = selectPrimaryPlacement(placements, overrideId)
  const hiddenCount = Math.max(0, placements.length - 1)

  // Every placement mutation announces itself to sibling tabs so their ONE
  // reconcile callback fires (plan stale-multi-tab glitch kill).
  const handleChanged = () => {
    publishReaderMutation(email.id, 'placements')
    onChanged?.()
  }

  const onAllocated = (summary?: EmailAllocationSummary) => {
    if (summary && onPatchEmail) {
      const next = [
        ...placements.filter(
          (placement) => placement.allocation_id !== summary.allocation_id,
        ),
        summary,
      ]
      onPatchEmail(email.id, {
        allocation_summary: selectPrimaryPlacement(next, overrideId),
        allocation_summaries: next,
      })
    }
    handleChanged()
  }

  return (
    <>
      <div
        className="flex min-w-0 flex-nowrap items-center gap-1 overflow-hidden"
        data-testid="reader-placement-summary"
        onContextMenu={(event) => {
          event.preventDefault()
          event.stopPropagation()
          // Empty FILED has nothing to manage — left-click is AllocateChip.
          // Right-click only opens the manager once there is at least one row.
          if (placements.length > 0) setOpen(true)
        }}
      >
        {primary ? (
          <AllocationChip
            emailId={email.id}
            summary={primary}
            api={api}
            orgId={orgId}
            onOpenCard={onOpenCard}
            onOpenBoard={
              onOpenBoard ? () => onOpenBoard(primary) : undefined
            }
            onChanged={handleChanged}
            // Always full breadcrumb in the reader — compact is for inbox
            // rows that already frame the board name (change #431).
            hideReviewControls={suppressReviewControls}
          />
        ) : (
          // BUG-2026-08-03-PLACEMENT-ADD-TWO-CLICK — empty FILED used to open
          // the Placement Manager (empty state) and require a second click on
          // footer "Add placement". Wire AllocateChip here so the first click
          // opens the board picker.
          <AllocateChip
            emailId={email.id}
            api={api}
            orgId={orgId}
            onAllocated={onAllocated}
            onConflict={(conflict) => {
              onOpenCard?.(conflict.card_id)
            }}
            label="Add placement"
          />
        )}
        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className={cn(
              'relative inline-flex min-h-7 items-center rounded-full border border-border bg-background px-2 text-[10px] font-medium text-muted-foreground hover:bg-accent',
              "before:absolute before:inset-0 before:content-[''] pointer-coarse:before:inset-[-8px]",
            )}
            aria-label={`Manage ${placements.length} placements`}
          >
            +{hiddenCount}
          </button>
        )}
        {primary && hiddenCount === 0 && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className={cn(
              'relative inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-accent group-hover:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100',
              // WORK ITEM 8: native pointer-coarse variant (was the
              // [@media(pointer:coarse)] arbitrary form) for consistency
              // with EllipsisSlot.tsx. WORK ITEM 7: transparent hit area.
              "before:absolute before:inset-0 before:content-[''] pointer-coarse:before:inset-[-8px]",
            )}
            aria-label="Manage placements"
            title="Manage placements"
          >
            <Layers3 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {open && (
        <PlacementManagerPanel
          email={email}
          placements={placements}
          primary={primary}
          overrideId={overrideId}
          api={api}
          orgId={orgId}
          surface={surface}
          onClose={() => setOpen(false)}
          onAllocated={onAllocated}
          onPatchEmail={onPatchEmail}
          onChanged={handleChanged}
          onOpenCard={onOpenCard}
          onOpenBoard={onOpenBoard}
        />
      )}
    </>
  )
}
